import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  CircleDollarSign,
  LetterText,
  Loader2,
  PlusCircle,
  ReceiptText,
} from "lucide-react";
import { createExpensePolicyClient } from "shared";
import { toast } from "sonner";
import type z from "zod";
import { Button } from "@/components/ui/button";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";

export const Route = createFileRoute("/_authed/_org/policies/expense/create")({
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const { mutate: createPolicy, isPending } = useMutation(
    trpc.expensePolicy.create.mutationOptions({
      onError: (error) => {
        toast.error("Failed to create policy", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success("Policy created successfully");
        navigate({
          to: "/policies/expense",
        });
        form.reset();
        queryClient.invalidateQueries(trpc.expensePolicy.list.queryOptions());
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      description: "",
      excludeVirtualReceipt: true,
      memoCents: 0,
      memoPrompt: "",
      name: "",
      receiptCents: 0,
      requireBudget: false,
      requireMemo: false,
      requireReceipt: false,
    } as z.infer<typeof createExpensePolicyClient>,
    onSubmit: ({ value }) => {
      // Only submit receipt/memo cents if the respective option is enabled
      createPolicy({
        ...value,
        memoCents: value.requireMemo ? value.memoCents : null,
        receiptCents: value.requireReceipt ? value.receiptCents : null,
      });
    },
    validators: {
      onSubmit: createExpensePolicyClient,
    },
  });
  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-b px-6 py-6 pb-2">
        <SheetTitle>Create a Policy</SheetTitle>
        <SheetDescription>
          Policies control when receipts and memos are required for transactions
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        {/* Scrollable content area with fixed max height */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-4">
          <div className="space-y-4">
            <form.AppField
              children={(field) => (
                <field.TextField label="Name" required={true} />
              )}
              name="name"
            />
            <form.AppField
              children={(field) => (
                <field.TextAreaField label="Description" required={false} />
              )}
              name="description"
            />
            <form.AppField
              children={(field) => (
                <field.TextField
                  description="Describe what details should be included in transaction memos"
                  label="Memo Prompt"
                />
              )}
              name="memoPrompt"
            />
            {/* <Toggle variant={"outline"}></Toggle> */}
            <div className="flex w-full flex-row space-x-2">
              <form.AppField
                children={(field) => (
                  <field.ToggleField variant={"outline"}>
                    <CircleDollarSign />
                    Require Budget
                  </field.ToggleField>
                )}
                name="requireBudget"
              />

              <form.AppField
                children={(field) => (
                  <field.ToggleField variant={"outline"}>
                    <LetterText />
                    Require Memo
                  </field.ToggleField>
                )}
                name="requireMemo"
              />
              <form.AppField
                children={(field) => (
                  <field.ToggleField variant={"outline"}>
                    <ReceiptText />
                    Require Receipt
                  </field.ToggleField>
                )}
                name="requireReceipt"
              />
            </div>

            <form.Subscribe
              selector={(state) => state.values.requireMemo}
              children={(required) =>
                required && (
                  <form.AppField
                    children={(field) => (
                      <field.MoneyField
                        description="Transactions below this value will not require a memo. Leave at $0.00 to require for all transactions."
                        descriptionStyle="below"
                        label="Memo Threshold"
                      />
                    )}
                    name="memoCents"
                  />
                )
              }
            />
            <form.Subscribe
              selector={(state) => state.values.requireReceipt}
              children={(required) =>
                required && (
                  <>
                    <form.AppField
                      children={(field) => (
                        <field.MoneyField
                          description="Transactions below this value will not require a receipt. Leave at $0.00 to require for all transactions."
                          descriptionStyle="below"
                          label="Receipt Threshold"
                        />
                      )}
                      name="receiptCents"
                    />
                    <form.AppField
                      children={(field) => (
                        <field.CheckboxField
                          description="Do not require receipts for virtual card purchases. "
                          label="Exempt Virtual Card Transactions"
                        />
                      )}
                      name="excludeVirtualReceipt"
                    />
                  </>
                )
              }
            />
          </div>
        </div>
        <div className="flex min-h-16 flex-row items-center justify-start border-t p-6">
          <Button
            className="font-semibold"
            disabled={isPending}
            size="default"
            type="submit"
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlusCircle className="mr-2 h-4 w-4" />
            )}
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
